import * as THREE from 'three';
import { emis, smoke, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Draco de vidrio (era V, docs/CRIATURAS.md «Era V · Somnhold»): a grown child of the Burn, the dragon. The
 * parent's black volcanic glass, but the fire is no longer a heart in a rift: the whole beast is full of it, and
 * it shows through every crack of the glass, orange to the dragon's red (#ff4f3a). A heavy, deep-chested drake on
 * great wings (as wide across as the frost colossus is tall), a crown of glass horns with the fire burning in
 * their roots, long fangs, a row of tall blades down its spine, the fan of glass at the tail's tip like the
 * parent's. Kin of the wyrmling at a glance (the same glass, the same ember) but grown: the head in proportion,
 * the horns long, the cracks wide open, the belly a lattice of fire.
 *
 * It flies: the hover is its rest pose (`DRAKE_HOVER`; `die` drops it). Bones as the wyrmling's (body; neck1–3,
 * head and jaw; `throat`; `breath`; tail1–5; each wing in arm, fore and hand; four legs, thigh and shin), but
 * `breath` is a wide cone of fire, not a jet: built tiny at the mouth (`SWEEP_REST`) so it is hidden until the
 * sweep clip scales it up. The right wing is +X. Opaque shared materials (the parent's glass), the ember its one
 * self-lit material, the membrane and the fire of the cone translucent, plus glow sprites (no lights).
 */

/** Height of the model's origin over the ground while it flies (the clips drop it from here to die). */
export const DRAKE_HOVER = 1.6;
/** How small the cone of fire is built (the sweep clip scales `breath` by about 1 / SWEEP_REST to show it). */
export const SWEEP_REST = 0.02;

/**
 * A membrane of glass from `hub` to the edge a→b: rings of quads bellying a little, the edge scalloped in toward
 * the hub between the fingers (as the wyrmling's).
 */
const panel = (pos: number[], idx: number[], hub: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3, sag: number, S: number, droop: number, flip: boolean): void => {
  const RN = 2;
  const E: THREE.Vector3[] = [];
  for (let j = 0; j <= S; j++) {
    const u = j / S;
    E.push(a.clone().lerp(b, u).lerp(hub, Math.sin(u * Math.PI) * sag));
  }
  const v = (p: THREE.Vector3): number => {
    pos.push(p.x, p.y, p.z);
    return pos.length / 3 - 1;
  };
  const h = v(hub);
  const rows: number[][] = [];
  for (let i = 1; i <= RN; i++) {
    const k = i / RN;
    rows.push(E.map((e, j) => v(hub.clone().lerp(e, k).add(V(0, -droop * Math.sin(k * Math.PI) * Math.sin((j / S) * Math.PI), 0)))));
  }
  const tri = (x: number, y: number, z: number): void => {
    if (flip) idx.push(x, z, y);
    else idx.push(x, y, z);
  };
  for (let j = 0; j < S; j++) tri(h, rows[0]![j]!, rows[0]![j + 1]!);
  for (let i = 1; i < RN; i++) {
    for (let j = 0; j < S; j++) {
      const a0 = rows[i - 1]![j]!, a1 = rows[i - 1]![j + 1]!, b0 = rows[i]![j]!, b1 = rows[i]![j + 1]!;
      tri(a0, b0, b1);
      tri(a0, b1, a1);
    }
  }
};

export const glassDrake = (o: BaseOpts = {}): Model => {
  const c = begin(o, 1.0, 5101);
  const { g, rng, R } = c;
  g.position.y = DRAKE_HOVER;

  // The parent's glass (the same cached materials as models/dragon.ts and the wyrmling): black, faceted, glossy;
  // the bluer glass of the plates; near-black for belly, jaw, teeth and claws.
  const glass = std('#121018', { roughness: 0.14, metalness: 0.25, flatShading: true, bumpScale: 0 });
  const plate = std('#2c2838', { roughness: 0.22, metalness: 0.35, flatShading: true, bumpScale: 0 });
  const dark = std('#08070b', { roughness: 0.3, metalness: 0.2, flatShading: true, bumpScale: 0 });
  // The fire inside, brighter than the wyrmling's: it is full of it.
  const ember = emis('#ffb27a', '#ff4f3a', 2.4);
  // The membranes: dark glass you can half see through, the fire glowing through them red.
  const skin = smoke('#2a1018', 0.78, { side: THREE.DoubleSide, emissive: '#5a1408', roughness: 0.2, metalness: 0.3 });
  // The cone of fire: translucent, self-lit.
  const fire = emis('#ff6a2a', '#ff4f3a', 1.5, { transparent: true, opacity: 0.55, depthWrite: false, side: THREE.DoubleSide });

  /** A faceted lump of glass. */
  const shard = (parent: THREE.Object3D, mat: THREE.Material, r: number, p: THREE.Vector3, sx = 1, sy = 1, sz = 1, detail = 1): THREE.Mesh => {
    const m = M(new THREE.IcosahedronGeometry(r, detail), mat, parent);
    m.position.copy(p);
    m.scale.set(sx, sy, sz);
    m.rotation.set(R() * 0.6, R() * 0.6, R() * 0.6);
    return m;
  };
  /** A blade of glass from `at` along `dir` (a plate, a spine, a claw), flattened sideways by `flat`. */
  const blade = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, flat = 0.45, seg = 4): THREE.Mesh => {
    const d = dir.clone().normalize();
    const m = M(new THREE.ConeGeometry(w, len, seg), mat, parent);
    m.position.copy(at).addScaledVector(d, len * 0.45);
    m.quaternion.setFromUnitVectors(V(0, 1, 0), d);
    m.scale.z = flat;
    return m;
  };
  /** A jagged crack of fire: doubled, jittered points joined by a glowing tube; a glow where it is widest. */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.016, glow = 0): void => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p);
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V(R() * 0.04, R() * 0.04, R() * 0.04)));
    });
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.05), jit.length - 1, r, 3), ember, parent);
    if (glow > 0) glowSprite(parent, '#ff6a1f', glow, jit[Math.floor(jit.length / 2)]!, 0.5);
  };

  /* ---------- bones ---------- */
  const bodyJ = joint(g, V(0, 0, 0), 'body');
  const NECK = [V(0, 0.12, 0.46), V(0, 0.28, 0.74), V(0, 0.44, 0.92), V(0, 0.54, 1.12)];
  const neckC = new THREE.CatmullRomCurve3(NECK);
  const n1 = joint(bodyJ.abs, NECK[0]!, 'neck1');
  const n2 = joint(n1.abs, neckC.getPoint(0.36), 'neck2');
  const n3 = joint(n2.abs, neckC.getPoint(0.7), 'neck3');
  const HEAD = V(0, 0.57, 1.2);
  const headJ = joint(n3.abs, HEAD, 'head');
  const head = headJ.abs;
  const jawJ = joint(head, V(0, 0.5, 1.28), 'jaw');
  const throatJ = joint(n3.abs, V(0, 0.4, 1.04), 'throat');
  const MOUTH = V(0, 0.5, 1.78);
  const breathJ = joint(head, MOUTH, 'breath');
  const body = bodyJ.abs;

  /* ---------- trunk: heavy, deep-chested, the fire in a lattice of cracks over it ---------- */
  shard(body, glass, 0.3, V(0, 0.04, 0.24), 1.0, 0.95, 1.25);
  shard(body, glass, 0.26, V(0, 0.02, -0.14), 0.95, 0.9, 1.4);
  shard(body, glass, 0.2, V(0, 0.0, -0.5), 1.0, 0.88, 1.25);
  shard(body, dark, 0.2, V(0, -0.13, 0.06), 0.95, 0.6, 1.9, 0);
  // Shoulders where the wings grow, plates of the bluer glass over them.
  for (const k of [-1, 1]) shard(body, plate, 0.14, V(k * 0.2, 0.18, 0.28), 1.1, 0.8, 1.3, 0);
  // The heart, behind a ring of blades in the chest: no longer small.
  M(new THREE.IcosahedronGeometry(0.11, 0), ember, body).position.set(0, 0.0, 0.58);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    blade(body, plate, V(Math.cos(a) * 0.1, Math.sin(a) * 0.1, 0.55), V(Math.cos(a), Math.sin(a), 0.7), 0.15, 0.04);
  }
  const heartGlow = glowSprite(body, '#ff5a1f', 0.85, V(0, 0.0, 0.68), 0.75, true);
  glowSprite(body, '#ff4f3a', 1.9, V(0, -0.05, 0.05), 0.22);
  // Spine: a ridge of tall blades, tallest over the shoulders, every third one the bluer glass.
  for (let i = 0; i < 8; i++) {
    const u = i / 7;
    const z = 0.46 - u * 1.0;
    blade(body, i % 3 === 1 ? plate : glass, V(0, 0.28 - u * 0.08 - Math.abs(u - 0.2) * 0.05, z), V(0, 1, -0.55), 0.3 - u * 0.12, 0.055);
  }
  // Plates along the flanks.
  for (let i = 0; i < 10; i++) {
    const k = i % 2 ? 1 : -1;
    const u = Math.floor(i / 2) / 4;
    const z = 0.36 - u * 0.86;
    blade(body, plate, V(k * (0.25 - u * 0.05), 0.07 + R() * 0.05, z), V(k, 0.6 + R() * 0.3, -0.6), 0.2 + rng() * 0.06, 0.065);
  }
  // The lattice of fire: wide cracks over chest, belly, flanks and haunches.
  crack(body, [V(-0.22, 0.14, 0.42), V(-0.1, 0.05, 0.54), V(0.08, 0.1, 0.54), V(0.22, 0.02, 0.42)], 0.02, 0.3);
  crack(body, [V(0.0, -0.22, 0.42), V(0.03, -0.25, 0.18), V(-0.03, -0.25, -0.08), V(0.02, -0.2, -0.34), V(0, -0.16, -0.56)], 0.02, 0.35);
  for (const k of [-1, 1]) {
    crack(body, [V(k * 0.29, 0.12, 0.28), V(k * 0.3, 0.0, 0.1), V(k * 0.26, -0.08, -0.1), V(k * 0.24, 0.05, -0.32), V(k * 0.22, -0.03, -0.56)], 0.018, 0.32);
    crack(body, [V(k * 0.12, -0.24, 0.3), V(k * 0.2, -0.2, 0.1), V(k * 0.18, -0.22, -0.18)], 0.014);
    crack(body, [V(k * 0.18, 0.2, -0.42), V(k * 0.22, 0.06, -0.62), V(k * 0.15, -0.1, -0.7)], 0.014);
    crack(body, [V(k * 0.08, 0.27, 0.2), V(k * 0.16, 0.22, 0.0), V(k * 0.12, 0.24, -0.22)], 0.012);
  }

  /* ---------- neck ---------- */
  const NN = 6;
  for (let i = 0; i < NN; i++) {
    const t = i / NN;
    const bone = t < 0.36 ? n1.abs : t < 0.7 ? n2.abs : n3.abs;
    const p = neckC.getPoint(t + 0.04);
    shard(bone, i % 2 ? glass : plate, 0.14 - t * 0.035, p, 1.05, 0.95, 1.2, 0);
    blade(bone, glass, p.clone().add(V(0, 0.11 - t * 0.03, -0.01)), V(0, 1, -0.8), 0.17 - t * 0.04, 0.035);
  }
  limb(n1.abs, NECK[0]!, neckC.getPoint(0.36), 0.14, 0.115, glass, 6);
  limb(n2.abs, neckC.getPoint(0.36), neckC.getPoint(0.7), 0.115, 0.1, glass, 6);
  limb(n3.abs, neckC.getPoint(0.7), NECK[3]!, 0.1, 0.09, glass, 6);
  // The fire runs in a seam down the underside of the neck, and in cracks along its sides.
  crack(n1.abs, [V(0, -0.02, 0.52), V(0.01, 0.1, 0.68), V(0, 0.2, 0.78)], 0.02, 0.25);
  crack(n2.abs, [V(0, 0.2, 0.78), V(-0.01, 0.29, 0.89), V(0, 0.34, 0.96)], 0.018);
  for (const k of [-1, 1]) crack(n2.abs, [V(k * 0.1, 0.3, 0.8), V(k * 0.09, 0.36, 0.9)], 0.01);

  /* ---------- the throat: fire behind ribs of glass ---------- */
  const throat = throatJ.abs;
  const core = M(new THREE.SphereGeometry(0.09, 6, 4), ember, throat);
  core.position.set(0, 0.4, 1.05);
  core.scale.set(0.85, 0.85, 1.5);
  for (let i = 0; i < 3; i++) {
    const rib = M(new THREE.TorusGeometry(0.088, 0.02, 3, 5, Math.PI * 1.1), dark, throat);
    rib.position.set(0, 0.405, 0.98 + i * 0.065);
    rib.rotation.set(0, 0, Math.PI * 1.45 + R() * 0.2);
    rib.scale.set(0.95 - Math.abs(i - 1) * 0.12, 0.9, 1);
  }
  const throatGlow = glowSprite(throat, '#ff5a2a', 0.6, V(0, 0.38, 1.07), 0.6, true);

  /* ---------- head: long-snouted, crowned with glass horns that burn at the roots ---------- */
  shard(head, glass, 0.17, V(0, 0.6, 1.3), 1.0, 0.78, 1.2);
  shard(head, plate, 0.11, V(0, 0.67, 1.22), 0.95, 0.5, 1.1, 0);
  const snout = M(new THREE.ConeGeometry(0.12, 0.5, 5), glass, head);
  snout.rotation.x = Math.PI / 2 + 0.07;
  snout.scale.set(1, 1, 0.66);
  snout.position.set(0, 0.57, 1.58);
  for (let i = 0; i < 4; i++) blade(head, plate, V(0, 0.64 - i * 0.015, 1.4 + i * 0.09), V(0, 1, -0.5), 0.1 - i * 0.014, 0.03);
  for (const k of [-1, 1]) {
    // Brows of plate over eyes of fire.
    blade(head, plate, V(k * 0.09, 0.66, 1.33), V(k * 0.35, 0.45, -1), 0.24, 0.05);
    M(new THREE.SphereGeometry(0.03, 5, 4), ember, head).position.set(k * 0.095, 0.62, 1.4);
    glowSprite(head, '#ffb36a', 0.3, V(k * 0.1, 0.625, 1.42), 1);
    // The fire along the lips and in a crack down the cheek.
    crack(head, [V(k * 0.1, 0.53, 1.3), V(k * 0.085, 0.52, 1.46), V(k * 0.055, 0.53, 1.62), V(k * 0.035, 0.54, 1.74)], 0.012);
    crack(head, [V(k * 0.12, 0.6, 1.34), V(k * 0.15, 0.57, 1.22), V(k * 0.15, 0.6, 1.1)], 0.01);
    // Long horns swept back and up, the bluer glass at the tips.
    const hp = [V(k * 0.08, 0.69, 1.24), V(k * 0.16, 0.77, 1.06), V(k * 0.22, 0.86, 0.88), V(k * 0.24, 0.98, 0.7), V(k * 0.22, 1.1, 0.56)];
    for (let i = 0; i < 4; i++) limb(head, hp[i]!, hp[i + 1]!, 0.05 - i * 0.01, 0.04 - i * 0.01, i === 3 ? plate : glass, 4);
    crack(head, [hp[0]!, hp[1]!, hp[2]!], 0.008);
    // Cheek blades flaring back along the jaw.
    blade(head, plate, V(k * 0.13, 0.56, 1.25), V(k * 0.75, -0.1, -1), 0.26, 0.04);
    blade(head, glass, V(k * 0.12, 0.5, 1.27), V(k * 0.8, -0.4, -1), 0.18, 0.032);
    // Nostrils that smoulder.
    M(new THREE.SphereGeometry(0.016, 4, 3), ember, head).position.set(k * 0.04, 0.59, 1.8);
  }
  // The crown: a ring of glass blades over a coal of fire, the parent's crown half grown.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    blade(head, i % 2 ? plate : glass, V(Math.cos(a) * 0.08, 0.7, 1.16 + Math.sin(a) * 0.08), V(Math.cos(a) * 0.45, 1, Math.sin(a) * 0.45 - 0.35), 0.17, 0.026);
  }
  M(new THREE.IcosahedronGeometry(0.05, 0), ember, head).position.set(0, 0.74, 1.15);
  const crownGlow = glowSprite(head, '#ff5a1f', 0.5, V(0, 0.8, 1.14), 0.6, true);
  // Long upper fangs, and the fire inside the mouth (seen when the jaw drops).
  for (let i = 0; i < 5; i++) for (const k of [-1, 1]) blade(head, dark, V(k * (0.07 - i * 0.008), 0.52, 1.36 + i * 0.08), V(0, -1, 0.15), i === 0 ? 0.1 : 0.06, 0.014, 1, 3);
  const maw = M(new THREE.SphereGeometry(0.08, 6, 4), ember, head);
  maw.position.set(0, 0.51, 1.44);
  maw.scale.set(0.8, 0.35, 2.1);
  const jaw = jawJ.abs;
  const lower = M(new THREE.ConeGeometry(0.085, 0.48, 5), dark, jaw);
  lower.rotation.x = Math.PI / 2 - 0.05;
  lower.scale.set(0.95, 1, 0.5);
  lower.position.set(0, 0.48, 1.52);
  for (let i = 0; i < 4; i++) for (const k of [-1, 1]) blade(jaw, dark, V(k * (0.055 - i * 0.008), 0.5, 1.38 + i * 0.08), V(0, 1, 0.15), 0.05, 0.012, 1, 3);
  for (const k of [-1, 1]) blade(jaw, plate, V(k * 0.07, 0.46, 1.32), V(k * 0.5, -0.5, -1), 0.14, 0.03);
  crack(jaw, [V(0, 0.44, 1.34), V(0, 0.43, 1.5), V(0, 0.45, 1.66)], 0.01);

  /* ---------- the breath: a wide cone of fire built tiny at the mouth, pointing ahead and down ---------- */
  {
    const bg = G(breathJ.abs, MOUTH.x, MOUTH.y, MOUTH.z);
    bg.scale.setScalar(SWEEP_REST);
    // Straight out of the snout (+Z): the sweep pitches the head down to lay it on the ground ahead.
    bg.rotation.x = 0.15;
    const L = 3.0, Rr = 1.5;
    const cone = (len: number, r: number, seg: number, z0: number, mat: THREE.Material): void => {
      const geo = new THREE.CylinderGeometry(r, 0.05, len, seg, 2, true);
      geo.rotateX(Math.PI / 2);
      geo.translate(0, 0, z0 + len / 2);
      // A ragged mouth: the far rim pushed in and out.
      const pos = geo.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        if (pos.getZ(i) > z0 + len * 0.9) pos.setZ(i, pos.getZ(i) + (rng() - 0.5) * len * 0.22);
      }
      geo.computeVertexNormals();
      M(geo, mat, bg);
    };
    cone(L, Rr, 10, 0, fire);
    cone(L * 0.82, Rr * 0.6, 8, 0.05, fire);
    cone(L * 0.55, Rr * 0.28, 6, 0.02, ember);
    // Tongues licking off it.
    for (let i = 0; i < 6; i++) {
      const a = i * 1.05 + 0.3;
      const t = M(new THREE.ConeGeometry(0.16, 0.9, 4, 1, true), fire, bg);
      t.position.set(Math.cos(a) * Rr * 0.7, Math.sin(a) * Rr * 0.7, L * (0.6 + (i % 3) * 0.12));
      t.rotation.set(Math.PI / 2 - Math.sin(a) * 0.5, 0, Math.cos(a) * 0.5);
    }
  }

  /* ---------- wings: arm, forearm and a hand of four long fingers, membranes of glass lit from within ---------- */
  const skel: Record<string, THREE.Object3D> = {
    body: bodyJ.p, neck1: n1.p, neck2: n2.p, neck3: n3.p, head: headJ.p, jaw: jawJ.p, throat: throatJ.p, breath: breathJ.p,
  };
  const sparks: { s: THREE.Sprite; p: THREE.Vector3; ph: number }[] = [];
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const X = (x: number, y: number, z: number): THREE.Vector3 => V(k * x, y, z);
    const sh = X(0.24, 0.2, 0.3), el = X(0.95, 0.52, 0.04), wr = X(1.66, 0.7, 0.22);
    const armJ = joint(body, sh, `arm${s}`);
    const foreJ = joint(armJ.abs, el, `fore${s}`);
    const handJ = joint(foreJ.abs, wr, `hand${s}`);
    skel[`arm${s}`] = armJ.p;
    skel[`fore${s}`] = foreJ.p;
    skel[`hand${s}`] = handJ.p;
    const arm = armJ.abs, fore = foreJ.abs, hand = handJ.abs;
    limb(arm, sh, el, 0.1, 0.065, glass, 6);
    shard(arm, glass, 0.12, X(0.44, 0.3, 0.2), 1.4, 0.85, 1.1, 0);
    shard(fore, plate, 0.075, el, 1, 1, 1, 0);
    limb(fore, el, wr, 0.065, 0.045, glass, 5);
    shard(hand, plate, 0.065, wr, 1.1, 1, 1, 0);
    crack(arm, [X(0.34, 0.28, 0.27), X(0.62, 0.4, 0.16), X(0.9, 0.52, 0.06)], 0.014);
    crack(fore, [X(1.0, 0.54, 0.06), X(1.3, 0.62, 0.14), X(1.6, 0.7, 0.2)], 0.011);
    blade(hand, plate, X(1.67, 0.73, 0.26), X(0.2, 0.35, 1), 0.2, 0.032, 1);
    blade(hand, glass, wr, X(0.2, 0.6, 0.5), 0.22, 0.045);
    const tips = [X(2.9, 0.86, 0.04), X(2.74, 0.52, -0.72), X(2.24, 0.24, -1.3), X(1.5, 0.05, -1.5)];
    for (const [i, tp] of tips.entries()) {
      const knuckle = wr.clone().lerp(tp, 0.45).add(V(0, 0.04, 0.03));
      limb(hand, wr, knuckle, 0.034, 0.024, glass, 3);
      limb(hand, knuckle, tp, 0.024, 0.006, i === 0 ? plate : glass, 3);
      M(new THREE.TubeGeometry(new THREE.LineCurve3(wr.clone().lerp(tp, 0.08), wr.clone().lerp(tp, 0.75)), 2, 0.011, 3), ember, hand);
    }
    const root = X(0.18, 0.0, -0.72);
    const parts: [THREE.Object3D, THREE.Vector3, THREE.Vector3, THREE.Vector3, number, number, number][] = [
      [hand, wr, tips[0]!, tips[1]!, 0.16, 5, 0.05],
      [hand, wr, tips[1]!, tips[2]!, 0.2, 5, 0.07],
      [hand, wr, tips[2]!, tips[3]!, 0.2, 5, 0.07],
      [fore, el, tips[3]!, root, 0.22, 6, 0.1],
      [arm, sh, el, root, 0.04, 4, 0.04],
    ];
    for (const [bone, hub, a, b, sag, S, droop] of parts) {
      const pos: number[] = [];
      const idx: number[] = [];
      panel(pos, idx, hub, a, b, sag, S, droop, k < 0);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      geo.setIndex(idx);
      geo.computeVertexNormals();
      M(geo, skin, bone);
    }
    // The fire glows through the membranes where they are stretched widest.
    glowSprite(hand, '#ff4f3a', 0.9, wr.clone().lerp(tips[1]!, 0.5).add(V(0, -0.05, 0)), 0.22);
    glowSprite(fore, '#ff4f3a', 0.8, el.clone().lerp(tips[3]!, 0.45), 0.18);
    for (const [i, tp] of tips.entries()) {
      sparks.push({ s: glowSprite(hand, '#ff6a2a', 0.14, tp, 0.75, true), p: tp.clone(), ph: rng() + i * 0.1 });
    }
  }

  /* ---------- tail: a long, heavy whip ending in a fan of glass ---------- */
  const tailC = new THREE.CatmullRomCurve3([V(0, 0.0, -0.66), V(0.04, -0.07, -1.2), V(0.14, -0.12, -1.72), V(0.04, -0.15, -2.24), V(-0.1, -0.1, -2.72)]);
  const tailJ: { p: THREE.Group; abs: THREE.Group }[] = [];
  for (let j = 0; j < 5; j++) {
    tailJ.push(joint(j ? tailJ[j - 1]!.abs : body, tailC.getPoint(j * 0.19), `tail${j + 1}`));
    skel[`tail${j + 1}`] = tailJ[j]!.p;
  }
  const tailBone = (t: number): THREE.Object3D => tailJ[Math.min(4, Math.floor(t / 0.19 + 1e-6))]!.abs;
  const TN = 10;
  for (let i = 0; i < TN; i++) {
    const t0 = i / TN, t1 = (i + 1) / TN;
    const bone = tailBone(t0 + 0.02);
    const a = tailC.getPoint(t0), b = tailC.getPoint(t1);
    limb(bone, a, b, 0.15 - t0 * 0.12, 0.15 - t1 * 0.12, glass, t0 < 0.5 ? 5 : 4);
    if (i % 2 === 0) shard(bone, i % 4 === 0 ? glass : plate, 0.13 - t0 * 0.1, a, 1.1, 0.9, 1.3, 0);
    if (i % 2 === 1 && t0 < 0.85) blade(bone, i % 4 === 1 ? plate : glass, a.clone().add(V(0, 0.11 - t0 * 0.08, 0)), V(0, 1, -0.8), 0.17 - t0 * 0.1, 0.034);
  }
  for (const [j, t0, k] of [[0, 0.03, 1], [1, 0.25, -1], [2, 0.46, 1], [3, 0.66, -1]] as const) {
    const r = 0.14 - t0 * 0.11;
    crack(tailJ[j]!.abs, [0, 0.06, 0.12, 0.17].map((d, i) => tailC.getPoint(t0 + d).add(V(k * r * 0.9, r * (0.5 - (i % 2) * 0.6), 0))), 0.012);
  }
  const tip = tailJ[4]!.abs;
  const tp = tailC.getPoint(1);
  for (let i = -2; i <= 2; i++) blade(tip, i % 2 ? glass : plate, tp, V(i * 0.4, 0.15, -1), 0.4 - Math.abs(i) * 0.07, 0.065);
  M(new THREE.SphereGeometry(0.035, 5, 4), ember, tip).position.copy(tp).add(V(0, 0.02, 0.01));
  glowSprite(tip, '#ff4f3a', 0.3, tp.clone().add(V(0, 0.03, 0)), 0.65);

  /* ---------- four legs, heavy, drawn up under it in flight, claws curled ---------- */
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    for (const fore of [true, false]) {
      const e = fore ? 'F' : 'B';
      const hip = fore ? V(k * 0.17, -0.08, 0.34) : V(k * 0.18, -0.06, -0.48);
      const knee = fore ? V(k * 0.25, -0.3, 0.22) : V(k * 0.26, -0.27, -0.26);
      const ank = fore ? V(k * 0.22, -0.46, 0.4) : V(k * 0.21, -0.45, -0.58);
      const legJ = joint(body, hip, `leg${e}${s}`);
      const shinJ = joint(legJ.abs, knee, `shin${e}${s}`);
      skel[`leg${e}${s}`] = legJ.p;
      skel[`shin${e}${s}`] = shinJ.p;
      shard(legJ.abs, glass, fore ? 0.11 : 0.13, hip.clone().lerp(knee, 0.35), 1, 1.25, 1.1, 0);
      limb(legJ.abs, hip, knee, fore ? 0.075 : 0.09, 0.055, glass, 5);
      limb(shinJ.abs, knee, ank, 0.052, 0.036, glass, 5);
      shard(shinJ.abs, dark, 0.048, ank, 1.1, 0.7, 1.3, 0);
      crack(legJ.abs, [hip.clone().add(V(k * 0.07, 0, 0)), hip.clone().lerp(knee, 0.5).add(V(k * 0.07, 0, 0)), knee.clone().add(V(k * 0.04, 0, 0))], 0.01);
      for (let i = 0; i < 3; i++) {
        const a = (i - 1) * 0.45;
        blade(shinJ.abs, plate, ank.clone().add(V(Math.sin(a) * 0.03 * k, -0.015, 0.015)), V(Math.sin(a) * 0.5 * k, -1, fore ? 0.6 : -0.6), 0.12, 0.02, 1, 3);
      }
    }
  }

  // Embers shed off its back and wings, drifting up and back and dying out.
  const embers: { s: THREE.Sprite; x: number; z: number; ph: number }[] = [];
  for (let i = 0; i < 6; i++) embers.push({ s: glowSprite(body, i % 3 ? '#ff6a2a' : '#ff4f3a', 0.12, V(0, 0, 0), 0.85, true), x: R() * 0.6, z: 0.35 - rng() * 0.9, ph: i / 6 });

  const t = (n: number): void => {
    for (const s of sparks) {
      const q = (s.ph + n * 0.29) % 1;
      s.s.position.set(s.p.x, s.p.y + q * 0.08, s.p.z - q * 0.24);
      s.s.material.opacity = 0.8 * (1 - q) * (q < 0.1 ? q / 0.1 : 1);
    }
    for (const e of embers) {
      const q = (e.ph + n * 0.23) % 1;
      e.s.position.set(e.x + Math.sin(q * 5 + e.ph * 9) * 0.07, 0.28 + q * 0.32, e.z - q * 0.6);
      e.s.material.opacity = 0.9 * (1 - q) * (q < 0.08 ? q / 0.08 : 1);
    }
    throatGlow.material.opacity = 0.55 + 0.15 * Math.sin(n * 2.1) + R() * 0.08;
    heartGlow.material.opacity = 0.65 + 0.15 * Math.sin(n * 1.3 + 1) + R() * 0.06;
    crownGlow.material.opacity = 0.5 + 0.15 * Math.sin(n * 2.7) + R() * 0.08;
  };
  t(0);
  return done(c, DRAKE_HOVER + 1.1, t, undefined, bonesOf(c, skel));
};
